import React, { createContext, useContext, useState, useEffect } from 'react';
import { api, setAccessToken } from '../api/client.js';

export interface UserProfile {
  id: string;
  username?: string;
  email: string;
  role: 'superadmin' | 'admin' | 'shopkeeper' | 'agent';
  shopId?: string | null;
}

export interface ShopProfile {
  id: string;
  name: string;
  phone: string;
  status: 'ACTIVE' | 'GRACE' | 'EXPIRED' | 'SUSPENDED';
  valid_until: string;
  grace_days: number;
  features: {
    orderReceivedEnabled: boolean;
    printingConfirmationEnabled: boolean;
    expiryNoticeEnabled: boolean;
    turnNextEnabled?: boolean;
  };
}

interface AuthContextType {
  user: UserProfile | null;
  shop: ShopProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requires2Fa: boolean;
  deviceWaitingApproval: boolean;
  unapprovedDeviceId: string | null;
  approvalSentTo: string | null;
  ipBlockedMsg: string | null;
  login: (identifier: string, password: string, deviceLabel?: string, targetPortal?: 'shop' | 'admin' | 'superadmin') => Promise<void>;
  verify2Fa: (identifier: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [shop, setShop] = useState<ShopProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requires2Fa, setRequires2Fa] = useState(false);
  const [deviceWaitingApproval, setDeviceWaitingApproval] = useState(false);
  const [unapprovedDeviceId, setUnapprovedDeviceId] = useState<string | null>(null);
  const [approvalSentTo, setApprovalSentTo] = useState<string | null>(() => {
    return typeof window !== 'undefined' ? localStorage.getItem('pb_approval_sent_to') : null;
  });
  const [ipBlockedMsg, setIpBlockedMsg] = useState<string | null>(null);

  const refreshProfile = async () => {
    try {
      const data = await api.getMe();
      setUser(data.user);
      setShop(data.shop);
      setDeviceWaitingApproval(false);
      setUnapprovedDeviceId(null);
    } catch {
      setUser(null);
      setShop(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshProfile();

    const handleDeviceRevoked = (e: any) => {
      console.warn('[Security] Browser access revoked by Super Admin. Logging out immediately.');
      setAccessToken(null);
      setUser(null);
      setShop(null);
      setDeviceWaitingApproval(true);
      const sentTo = e?.detail?.approvalSentTo || e?.approvalSentTo;
      if (sentTo) {
        setApprovalSentTo(sentTo);
        localStorage.setItem('pb_approval_sent_to', sentTo);
      }
      setUnapprovedDeviceId(localStorage.getItem('pb_browser_device_id') || 'Current Browser');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?reason=access_revoked';
      }
    };

    window.addEventListener('auth_device_revoked', handleDeviceRevoked);

    // Continuous 4-second hardware security heartbeat to verify device authorization remains active
    const verifyInterval = setInterval(() => {
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        api.getMe().catch((err: any) => {
          if (err?.code === 'DEVICE_UNAPPROVED' || err?.status === 401 || err?.status === 403) {
            handleDeviceRevoked(err);
          }
        });
      }
    }, 4000);

    return () => {
      window.removeEventListener('auth_device_revoked', handleDeviceRevoked);
      clearInterval(verifyInterval);
    };
  }, []);

  const login = async (
    identifier: string,
    password: string,
    deviceLabel?: string,
    targetPortal?: 'shop' | 'admin' | 'superadmin'
  ) => {
    setDeviceWaitingApproval(false);
    setUnapprovedDeviceId(null);
    setIpBlockedMsg(null);
    setRequires2Fa(false);

    try {
      const res = await api.login({
        username: identifier,
        email: identifier,
        password,
        targetPortal,
        deviceLabel:
          deviceLabel ||
          (typeof navigator !== 'undefined'
            ? `${navigator.userAgent.includes('Chrome') ? 'Google Chrome' : 'Web Browser'} (${navigator.platform || 'PC'})`
            : 'Browser'),
      });
      if (res.requires2Fa) {
        setRequires2Fa(true);
        return;
      }

      setAccessToken(res.accessToken);
      setUser(res.user);
      await refreshProfile();
    } catch (err: any) {
      if (err.code === 'DEVICE_UNAPPROVED') {
        setDeviceWaitingApproval(true);
        const sentTo = err.approvalSentTo || err.details?.approvalSentTo;
        if (sentTo) {
          setApprovalSentTo(sentTo);
          localStorage.setItem('pb_approval_sent_to', sentTo);
        }
        setUnapprovedDeviceId(err.details?.deviceId || err.deviceId || localStorage.getItem('pb_browser_device_id') || 'Unknown');
      } else if (err.code === 'IP_BLOCKED') {
        setIpBlockedMsg(err.message || 'Access blocked from your IP address.');
      }
      throw err;
    }
  };

  const verify2Fa = async (email: string, token: string) => {
    try {
      const res = await api.verify2Fa({ email, token });
      setAccessToken(res.accessToken);
      setUser(res.user);
      setRequires2Fa(false);
      await refreshProfile();
    } catch (err) {
      throw err;
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      setAccessToken(null);
      setUser(null);
      setShop(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        shop,
        isAuthenticated: !!user,
        isLoading,
        requires2Fa,
        deviceWaitingApproval,
        unapprovedDeviceId,
        approvalSentTo,
        ipBlockedMsg,
        login,
        verify2Fa,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
